// Skin-share utility — turns the user's unlocked comet into a 1200×630
// social-card PNG (Twitter / Open Graph spec) and either invokes the
// native Web Share sheet (mobile / Android) or falls back to clipboard
// copy + automatic PNG download (desktop). Designed as the cheapest
// possible acquisition loop in a cosmetic-driven game: pilots screenshot
// their swag for free.
import { drawComet } from "./comet";

// Canvas dimensions — match Twitter / Open Graph card spec so the image
// renders edge-to-edge in tweet/X / Discord / iMessage previews without
// cropping awkwardly.
const CARD_W = 1200;
const CARD_H = 630;

function buildShareUrl(skin, refCode = null) {
  // Deep-link to the /skins page with the skin pre-selected via the
  // `skin` query param. The Skins page reads `?skin=<id>` and scrolls
  // the matching card into view + applies a one-shot highlight pulse.
  // UTM tags are appended so you can attribute viral traffic per-skin
  // in `/admin` analytics — the only way to know which cosmetics drive
  // the loudest acquisition.
  // The optional `ref` param carries the sharer's `ref_code` so when
  // the visitor signs up, the original pilot is credited a +50 moonstone
  // bonus (refer-and-earn). Backend dedupes per (referrer, new_user)
  // pair — replaying the link earns nothing.
  const origin = (typeof window !== "undefined" && window.location?.origin) || "";
  const params = new URLSearchParams({
    skin: skin.id,
    utm_source: "skin-share",
    utm_medium: "social",
    utm_campaign: skin.id,
  });
  if (refCode) params.set("ref", refCode);
  return `${origin}/skins?${params.toString()}`;
}

function buildShareText(skin) {
  return `Just unlocked ${skin.name} on Cosmic Worm Breaker — try it:`;
}

// Render the social card. Uses the same `drawComet` engine so the share
// asset matches the in-game art exactly. Returns a PNG Blob via the
// Promise resolved by `canvas.toBlob`.
export async function buildSkinShareCard(skin) {
  const canvas = document.createElement("canvas");
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext("2d");

  // --- Background ---------------------------------------------------------
  ctx.fillStyle = "#05050a";
  ctx.fillRect(0, 0, CARD_W, CARD_H);
  // Deep-violet vignette
  const vg = ctx.createRadialGradient(CARD_W * 0.5, CARD_H * 0.6, 0, CARD_W * 0.5, CARD_H * 0.6, CARD_W * 0.7);
  vg.addColorStop(0, "rgba(155,33,255,0.18)");
  vg.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, CARD_W, CARD_H);
  // Sparse starfield (deterministic seed from skin id)
  const seed = skin.id.length * 17;
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  for (let i = 0; i < 80; i++) {
    const sx = (i * 73.13 + seed) % CARD_W;
    const sy = (i * 31.7 + seed * 0.7) % CARD_H;
    const sr = i % 4 === 0 ? 1.6 : 0.8;
    ctx.beginPath();
    ctx.arc(sx, sy, sr, 0, Math.PI * 2);
    ctx.fill();
  }
  // Diagonal background comet streak (matches in-game backdrop)
  ctx.save();
  ctx.translate(CARD_W * 0.18, CARD_H * 0.85);
  ctx.rotate(-0.32);
  ctx.globalCompositeOperation = "lighter";
  const cg = ctx.createLinearGradient(-CARD_W * 0.45, 0, CARD_W * 0.05, 0);
  cg.addColorStop(0,    "rgba(155,33,255,0)");
  cg.addColorStop(0.55, "rgba(155,33,255,0.22)");
  cg.addColorStop(0.85, "rgba(255,61,240,0.4)");
  cg.addColorStop(1,    "rgba(255,255,255,0.6)");
  ctx.fillStyle = cg;
  ctx.beginPath();
  ctx.ellipse(-CARD_W * 0.2, 0, CARD_W * 0.27, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // --- Hero comet (large, dramatic) ---------------------------------------
  // Ball positioned right-of-center so the long tail sweeps left across
  // the bulk of the card — mirrors the in-game comet pose.
  drawComet(ctx, skin, CARD_W * 0.72, CARD_H * 0.5, 38, {
    angle: Math.PI, // pointing left → tail extends right→left
    trail: [],
    glowBoost: 1.2,
  });

  // --- Foreground text ----------------------------------------------------
  // Skin name (huge, gradient via composite)
  ctx.save();
  ctx.font = "900 92px 'Space Grotesk', 'Inter', system-ui, sans-serif";
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  // Soft glow halo
  ctx.shadowColor = skin.flame?.[1] || "#9b51ff";
  ctx.shadowBlur = 28;
  ctx.fillStyle = "#ffffff";
  ctx.fillText(skin.name.toUpperCase(), 64, CARD_H * 0.55);
  ctx.shadowBlur = 0;
  // Subtitle line
  ctx.font = "600 26px 'JetBrains Mono', monospace";
  ctx.fillStyle = skin.flame?.[0] || "#3aa8ff";
  ctx.fillText("// COSMIC COMET", 66, CARD_H * 0.43);
  // "JUST UNLOCKED" eyebrow
  ctx.font = "500 18px 'JetBrains Mono', monospace";
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.fillText("JUST UNLOCKED ON", 66, CARD_H * 0.32);
  // Game branding (bottom-left)
  ctx.font = "800 32px 'Space Grotesk', sans-serif";
  ctx.fillStyle = "#ffffff";
  ctx.fillText("COSMIC WORM BREAKER", 64, CARD_H * 0.85);
  // CTA (bottom-right)
  ctx.textAlign = "right";
  ctx.font = "500 18px 'JetBrains Mono', monospace";
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.fillText("CLAIM YOURS →", CARD_W - 64, CARD_H * 0.85);
  // Outer cyan border (matches in-game playfield)
  ctx.strokeStyle = "rgba(0,255,255,0.4)";
  ctx.lineWidth = 2;
  ctx.strokeRect(20, 20, CARD_W - 40, CARD_H - 40);
  ctx.restore();

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Failed to encode share card"));
    }, "image/png", 0.95);
  });
}

// Top-level share entry point. Tries native Web Share with the file
// (best UX — opens iOS/Android share sheet). Falls back to clipboard
// copy + automatic PNG download. Returns one of: "shared", "copied",
// or throws on full failure.
//
// `refCode` (optional) is the sharing pilot's referral code — embedded
// in the deep-link URL as `?ref=<code>` so the registration flow can
// credit them on signup.
export async function shareSkin(skin, refCode = null) {
  const text = buildShareText(skin);
  const url = buildShareUrl(skin, refCode);
  const blob = await buildSkinShareCard(skin);
  const file = new File([blob], `${skin.id}-comet.png`, { type: "image/png" });

  // Native Web Share with files — Android Chrome, iOS Safari
  if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text, url, title: `${skin.name} · Cosmic Worm Breaker` });
      return "shared";
    } catch (e) {
      // User cancelled — not an error condition. Re-throw real errors.
      if (e && e.name === "AbortError") return "cancelled";
      // Otherwise fall through to clipboard fallback.
    }
  }

  // Desktop fallback: copy share text + URL, download the PNG so the
  // pilot can drag it into their tweet/Discord composer.
  try {
    await navigator.clipboard.writeText(`${text} ${url}`);
  } catch { /* clipboard blocked — still let the download succeed */ }
  // Trigger the download
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(a.href);
    a.remove();
  }, 1500);
  return "copied";
}
